import { useState, useRef, useEffect } from "react";
import { ChevronDown, ChevronRight, Plus, Check, Trash2, ExternalLink, Copy, Square, SquareCheckBig, Search, Lock } from "lucide-react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  useDismiss,
  useHover,
  useRole,
  useInteractions,
  FloatingPortal,
} from "@floating-ui/react";
import { viewportClamp } from "../lib/floating";
import { useCreateTable, useUpdateTable, useDeleteTable } from "../api";
import { useToast } from "@/components/ux/Toast";
import { useConfirm } from "@/components/ConfirmModal";
import { RestaurantHoverCard, type PastVisit } from "./RestaurantHoverCard";

interface Table {
  id: string;
  reservationName: string;
  capacity: number;
  isLocked: boolean;
  users: any[];
  restaurantName?: string;
  restaurantId?: string;
  restaurantNeighborhoodName?: string;
  restaurantBookingLink?: string;
  restaurantTags?: string[];
  reservationDone?: Date | string | null;
}

interface Restaurant {
  id: string;
  name: string;
  neighborhoodId: string;
  neighborhoodName?: string;
  isActive?: boolean;
  tags?: string[] | null;
  type?: string;
}

interface NeighborhoodGroup {
  neighborhoodName: string;
  cityName: string;
  tables: Table[];
  totalCapacity: number;
  totalOccupied: number;
  totalBookings: number;
}

interface User {
  id: string;
  name: string;
  email: string;
  neighborhood?: string;
  score?: number;
  quizAnswers?: any;
  specialRequests?: string;
}

interface UserHistory {
  previousRestaurants: { id: string; name: string; visitedAt: string }[];
  previousTableMates: { id: string; name: string; metAt: string }[];
}

interface TableManagementSectionProps {
  tables: Table[];
  allRestaurants: Restaurant[];
  eventId: string;
  eventDate?: string;
  eventTime?: string;
  onRefresh: () => void;
  onToggleLock?: (tableId: string) => void;
  unassignedUsers?: User[];
  availableNeighborhoods?: string[];
  neighborhoodToCityMap?: Map<string, string>;
  userHistory?: Record<string, UserHistory>;
}

// Fixed list of reservation names (from OpenTable admin accounts)
const RESERVATION_NAMES = [
  "Lily Adams",
  "Sam Allen",
  "Indigo Anderson",
  "Kate Baker",
  "Ainsley Barnes",
  "Shawn Barnes",
  "Kevin Bell",
  "Fiona Black",
  "Noah Brooks",
  "Daisy Brown",
  "Sage Brown",
  "Lucy Burns",
  "Sam Butler",
  "Sawyer Butler",
  "Amy Carter",
  "Leslie Carter",
  "Tom Clark",
  "Ryan Cole",
  "Miles Cook",
  "John Cooper",
  "Mia Cooper",
  "Quinn Cooper",
  "Glenn Cox",
  "Max Davis",
  "Chris Evans",
  "Tess Fisher",
  "Jack Ford",
  "Morgan Foster",
  "Vera Foster",
  "Will Grant",
  "Iris Gray",
  "Reagan Gray",
  "Holly Green",
  "Emma Hall",
  "Francis Hall",
  "Logan Hall",
  "Luke Hart",
  "Frank Hill",
  "Maya Hunt",
  "Eddie Jones",
  "Claire Jones",
  "Cameron Kelly",
  "Sara Kelly",
  "Terry Kelly",
  "George King",
  "Harper King",
  "Victor Lane",
  "Jane Lewis",
  "Grace Long",
  "Ruby Mason",
  "Adam Miller",
  "Henry Moore",
  "Rose Morgan",
  "Sydney Morgan",
  "Owen Page",
  "Pheonix Parker",
  "Zoey Parker",
  "Nora Perry",
  "Alex Phillips",
  "Olive Price",
  "Jamie Ramirez",
  "Mark Reed",
  "Dallas Rodriguez",
  "Ian Ross",
  "Eric Scott",
  "Beth Smith",
  "Blake Stewart",
  "Alex Stone",
  "Val Sullivan",
  "Ben Taylor",
  "Sydney Torres",
  "Campbell Turner",
  "Jade Walsh",
  "Brennan Ward",
  "David Ward",
  "Leo West",
  "Anna White",
  "Frankie White",
  "Sam Wood",
  "Paul Young",
  "Lucas Brown",
  "Leya Holland",
  "Daniel Harris",
  "Sarah Collins",
  "Andrew Miller",
  "Ryan Anderson",
  "Ella Bennett",
  "Jake Bishop",
  "Nina Blake",
  "Cole Bradley",
  "Zoe Burke",
  "Dean Carson",
  "Ivy Chase",
  "Rory Dunn",
  "Megan Dale",
  "Kyle Dixon",
  "Lena Drake",
  "Rex Ellis",
  "Clara Finch",
  "Wade Flynn",
  "Tara Fox",
  "Drew Hale",
  "Jenna Hayes",
  "Colin Heath",
  "Abby Horn",
  "Felix Hyde",
  "Luna James",
  "Brett Kane",
  "Cara Kent",
  "Shane Knox",
  "Dawn Lamb",
  "Troy Lang",
  "Eve Lawson",
  "Neil Marsh",
  "Ava Mills",
  "Clay Nash",
  "Jude Neal",
  "Penny Noel",
  "Todd Owen",
  "Lila Palmer",
  "Cody Parks",
  "April Penn",
  "Finn Quinn",
  "Dana Reese",
  "Keith Rice",
  "Amber Roy",
  "Simon Rush",
  "Tina Shaw",
  "Clark Sloan",
  "Paige Snow",
  "Gabe Swift",
  "Gia Tate",
  "Marco Todd",
  "Liam Tucker",
  "Nell Vance",
  "June Vaughn",
  "Carl Wade",
  "Piper Watts",
  "Brady Webb",
  "Demi Wells",
  "Sean Wolf",
  "Elise York",
  "Hugo Bates",
  "Pearl Boyd",
  "Lance Carr",
  "Rita Chen",
  "Beau Cross",
  "Alice Day",
  "Reed Eaton",
  "Gwen Frost",
  "Kent Gibbs",
  "Hope Glass",
  "Alan Holt",
  "Elsa Irwin",
  "Ray Kemp",
  "Mae Klein",
  "Wes Lake",
  "Chloe Lowe",
  "Nate Lynch",
  "Ruth Mann",
  "Kirk Mercer",
  "Gail Norris",
  "Theo Olsen",
  "Marlo Pace",
  "Vince Pike",
  "Elle Ramsey",
  "Brent Rowe",
  "Callie Sims",
  "Derek Sharp",
  "Hazel Thorne",
  "Robin Troy",
  "Belle Ware",
  "Gavin Wren",
  "Sadie Yates",
  "Myles Zane",
  "Josie Blair",
  "Anton Doyle",
  "Cora Emery",
  "Grant Farley",
  "Heidi Glenn",
  "Ivan Howe",
  "Kara Jade",
  "Lars Keane",
  "Mona Leigh",
  "Nolan Marks",
  "Opal Novak",
];

const getRandomElement = <T,>(arr: T[]): T => {
  return arr[Math.floor(Math.random() * arr.length)];
};

/**
 * Get a random available reservation name that hasn't been used yet
 * @param usedNames - Set of names already in use by other tables
 * @returns A random unused name, or a fallback if all are taken
 */
const getNextAvailableReservationName = (usedNames: Set<string>): string => {
  const availableNames = RESERVATION_NAMES.filter((name) => !usedNames.has(name));
  if (availableNames.length > 0) {
    return getRandomElement(availableNames);
  }
  // Fallback: if all names are used, return a random one with a number suffix
  return `${getRandomElement(RESERVATION_NAMES)} ${usedNames.size + 1}`;
};

/**
 * Build a booking link with capacity and event date/time parameters
 * @param bookingLink - The base restaurant booking link
 * @param capacity - Number of people for the reservation
 * @param eventDate - Event date in YYYY-MM-DD format
 * @param eventTime - Event time in HH:MM format
 * @returns Complete booking link with parameters, or null if no base link
 */
const buildBookingLink = (
  bookingLink: string | undefined,
  capacity: number,
  eventDate: string | undefined,
  eventTime: string | undefined,
  reservationName: string | undefined
): string | null => {
  if (!bookingLink) return null;
  
  try {
    // Combine date and time into ISO format
    const dateTimeString = `${eventDate}T${eventTime}:00`;

    const encodedDateTime = encodeURIComponent(dateTimeString);
    
    // Check if URL already has query parameters
    const separator = bookingLink.includes('?') ? '&' : '?';
    
    return `${bookingLink}${separator}p=${capacity}&sd=${encodedDateTime}&bookingName=${encodeURIComponent(reservationName || "")}`;
  } catch (error) {
    console.error('Error building booking link:', error);
    return bookingLink;
  }
};

const extractDollarTags = (tags?: string[]): string[] => {
  if (!tags) return [];
  return tags.filter((t) => t.includes("$"));
};

// Venue types are "restaurant" | "cafe" | "bar" (see Neighborhoods.tsx
// classification from Google's primaryType); anything else renders as a
// restaurant.
const venueTypeEmoji = (type?: string): string => {
  if (type === "bar") return "🍸";
  if (type === "cafe") return "☕";
  return "🍽️";
};

const venueGroup = (r: Restaurant): "restaurant" | "cafe" | "bar" =>
  r.type === "bar" ? "bar" : r.type === "cafe" ? "cafe" : "restaurant";

interface ParsedUserPrefs {
  userName: string;
  budgets: string[];
  otherRequests: string[];
}

const parseUserPreferences = (users: any[]): ParsedUserPrefs[] => {
  return users.map((u) => {
    const info: ParsedUserPrefs = {
      userName: u.name || "Unknown",
      budgets: [],
      otherRequests: [],
    };
    if (!u.specialRequests) return info;
    try {
      const prefs = JSON.parse(u.specialRequests);
      if (Array.isArray(prefs.budget)) info.budgets = prefs.budget;
      if (prefs.specialRequestsText)
        info.otherRequests.push(prefs.specialRequestsText);
    } catch {
      // ignore
    }
    return info;
  });
};

function UserPreferencesIndicator({ users }: { users: any[] }) {
  const userPrefs = parseUserPreferences(users);
  const uniqueBudgets = Array.from(
    new Set(userPrefs.flatMap((u) => u.budgets))
  ).sort();
  const allRequests = userPrefs.flatMap((u) => u.otherRequests);

  const [isOpen, setIsOpen] = useState(false);
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    middleware: [offset(6), flip(), ...viewportClamp(384)],
    whileElementsMounted: autoUpdate,
    placement: "top",
  });
  const hover = useHover(context, {
    delay: { open: 150, close: 100 },
    move: false,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    dismiss,
    role,
  ]);

  if (uniqueBudgets.length === 0 && allRequests.length === 0) return null;

  const inlineRequests = allRequests.map((r) => `"${r}"`).join(" · ");

  return (
    <>
      <span
        ref={refs.setReference}
        {...getReferenceProps()}
        className="flex items-center gap-2 text-xs text-base-content/70 min-w-0 flex-1 overflow-hidden"
      >
        {uniqueBudgets.length > 0 && (
          <span className="font-medium shrink-0">
            {uniqueBudgets.join(" · ")}
          </span>
        )}
        {allRequests.length > 0 && (
          <span className="italic text-base-content/60 truncate min-w-0">
            {inlineRequests}
          </span>
        )}
      </span>
      {isOpen && allRequests.length > 0 && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-[9999] bg-dark-surface border border-dark-border rounded-lg shadow-2xl max-w-sm p-3"
          >
            <ul className="text-xs text-dark-muted space-y-1 list-disc list-inside">
              {allRequests.map((r, i) => (
                <li key={i} className="italic">
                  "{r}"
                </li>
              ))}
            </ul>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

interface RestaurantSelectCellProps {
  currentRestaurantId: string;
  currentRestaurantName: string;
  dollarTags: string[];
  availableRestaurants: Restaurant[];
  isLocked: boolean;
  onSelect: (restaurantId: string) => void;
  getPastVisits: (restaurantId: string | undefined, tableUsers: any[]) => PastVisit[];
  tableUsers: any[];
}

function RestaurantSelectCell({
  currentRestaurantId,
  currentRestaurantName,
  dollarTags,
  availableRestaurants,
  isLocked,
  onSelect,
  getPastVisits,
  tableUsers,
}: RestaurantSelectCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      setIsOpen(open);
      if (!open) setSearchQuery("");
    },
    middleware: [offset(4), flip(), ...viewportClamp(256)],
    whileElementsMounted: autoUpdate,
    placement: "bottom-start",
  });

  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
  }, [isOpen]);

  const filtered = searchQuery
    ? availableRestaurants.filter((r) =>
        r.name.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : availableRestaurants;

  const currentType = availableRestaurants.find((r) => r.id === currentRestaurantId)?.type;

  return (
    <>
      <RestaurantHoverCard
        restaurantId={currentRestaurantId}
        pastVisits={getPastVisits(currentRestaurantId, tableUsers)}
        tapToOpen={false}
      >
        <button
          ref={refs.setReference}
          {...getReferenceProps()}
          type="button"
          onClick={() => {
            if (isLocked) return;
            setIsOpen(!isOpen);
          }}
          className="py-1.5 text-left flex items-center gap-1 hover:text-primary transition-colors cursor-pointer w-56 max-w-full"
          disabled={isLocked}
          title={currentRestaurantName}
        >
          <span className="shrink-0">{venueTypeEmoji(currentType)}</span>
          <span className="border-b border-dotted border-base-content/30 hover:border-primary truncate min-w-0">
            {currentRestaurantName}
          </span>
          {dollarTags.length > 0 && (
            <span className="text-xs text-base-content/60 font-medium shrink-0">
              {dollarTags.join(" ")}
            </span>
          )}
          {(() => {
            const repeats = getPastVisits(currentRestaurantId, tableUsers);
            if (repeats.length === 0) return null;
            return (
              <span
                className="text-xs text-warning shrink-0"
                title={`Repeat visitors: ${repeats.map((v) => v.firstName).join(", ")}`}
              >
                ({repeats.length})
              </span>
            );
          })()}
          <ChevronDown
            className={`w-3 h-3 shrink-0 text-base-content/40 transition-transform ${
              isOpen ? "rotate-180" : ""
            }`}
          />
        </button>
      </RestaurantHoverCard>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="z-[60] w-64 bg-base-100 border border-base-300 rounded-lg shadow-xl overflow-hidden"
          >
            <div className="p-1.5 border-b border-base-300">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-base-content/40" />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="input input-xs input-bordered w-full pl-6"
                />
              </div>
            </div>
            <div className="max-h-48 overflow-y-auto">
              {filtered.length === 0 ? (
                <div className="px-3 py-3 text-xs text-base-content/50 text-center">
                  No restaurants found
                </div>
              ) : (
                (() => {
                  // Cafes and bars are appended after dividers so restaurants
                  // stay visually primary. Dividers render on group
                  // transitions (not first occurrence) because the current
                  // selection may be prepended out of group order.
                  return filtered.map((r, index) => {
                    const visits = getPastVisits(r.id, tableUsers);
                    const rDollarTags = extractDollarTags(r.tags || undefined);
                    const group = venueGroup(r);
                    const dividerLabel =
                      index > 0 && group !== venueGroup(filtered[index - 1]) && group !== "restaurant"
                        ? group === "cafe"
                          ? "☕ Cafes"
                          : "🍸 Bars"
                        : null;
                    return (
                      <div key={r.id}>
                        {dividerLabel && (
                          <div className="px-3 py-1 text-[10px] uppercase tracking-wide text-base-content/40 bg-base-200/50 border-y border-base-300">
                            {dividerLabel}
                          </div>
                        )}
                        <RestaurantHoverCard
                          restaurantId={r.id}
                          placement="right-start"
                          pastVisits={visits}
                          tapToOpen={false}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              onSelect(r.id);
                              setIsOpen(false);
                              setSearchQuery("");
                            }}
                            className={`w-full text-left px-3 py-1.5 text-xs hover:bg-base-200 flex items-center justify-between gap-2 transition-colors ${
                              currentRestaurantId === r.id ? "bg-primary/10 text-primary" : ""
                            }`}
                          >
                            <span className="truncate">
                              {venueTypeEmoji(r.type)} {r.name}
                              {rDollarTags.length > 0 && (
                                <span className="text-base-content/60 font-medium ml-1">
                                  {rDollarTags.join(" ")}
                                </span>
                              )}
                              {r.isActive === false && (
                                <span className="text-base-content/40 ml-1">(Inactive)</span>
                              )}
                              {visits.length > 0 && (
                                <span className="text-warning ml-1">({visits.length})</span>
                              )}
                            </span>
                            {currentRestaurantId === r.id && <Check className="w-3 h-3 shrink-0" />}
                          </button>
                        </RestaurantHoverCard>
                      </div>
                    );
                  });
                })()
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

export function TableManagementSection({
  tables,
  allRestaurants,
  eventId,
  eventDate,
  eventTime,
  onRefresh,
  onToggleLock,
  unassignedUsers = [],
  availableNeighborhoods = [],
  neighborhoodToCityMap = new Map(),
  userHistory,
}: TableManagementSectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [collapsedNeighborhoods, setCollapsedNeighborhoods] = useState<Set<string>>(new Set());
  const [localEdits, setLocalEdits] = useState<Record<string, {
    restaurantId: string;
    reservationName: string;
    capacity: number;
  }>>({});
  const [bulkCapacity, setBulkCapacity] = useState<Record<string, number>>({});
  const [isBulkCreating, setIsBulkCreating] = useState(false);

  const getPastVisits = (restaurantId: string | undefined, tableUsers: any[]): PastVisit[] => {
    if (!restaurantId || !userHistory) return [];
    const visits: PastVisit[] = [];
    for (const user of tableUsers) {
      const history = userHistory[user.id];
      if (!history) continue;
      for (const r of history.previousRestaurants) {
        if (r.id === restaurantId) {
          visits.push({ firstName: user.name?.split(" ")[0] || "Unknown", date: r.visitedAt });
        }
      }
    }
    return visits;
  };

  const toast = useToast();
  const { confirm } = useConfirm();
  const createTable = useCreateTable(eventId);
  const updateTable = useUpdateTable();
  const deleteTable = useDeleteTable(eventId);

  // Group tables by neighborhood. Keys are lowercased so case-mismatches
  // between the user-derived neighborhood list and the restaurant's stored
  // neighborhood name don't split the same neighborhood into multiple groups.
  const groupedByNeighborhood = new Map<
    string,
    { displayName: string; tables: Table[] }
  >();

  // Initialize a group for every neighborhood that has bookings OR an active
  // non-bar restaurant. This surfaces neighborhoods the admin can plausibly
  // place a table in — even when there are no tables/bookings there yet —
  // and protects against case-mismatches between user-derived neighborhood
  // names and restaurant neighborhood names.
  availableNeighborhoods.forEach((neighborhood) => {
    const key = neighborhood.toLowerCase();
    if (!groupedByNeighborhood.has(key)) {
      groupedByNeighborhood.set(key, { displayName: neighborhood, tables: [] });
    }
  });

  allRestaurants.forEach((restaurant) => {
    if (restaurant.isActive === false) return;
    const displayName = restaurant.neighborhoodName;
    if (!displayName) return;
    const key = displayName.toLowerCase();
    if (!groupedByNeighborhood.has(key)) {
      groupedByNeighborhood.set(key, { displayName, tables: [] });
    }
  });

  // Include all tables (bars + restaurants) — both can host a booking. Bars
  // surface at the bottom of the restaurant picker via a divider.
  const visibleTables = tables;

  visibleTables.forEach((table) => {
    const displayName = table.restaurantNeighborhoodName || "Unknown";
    const key = displayName.toLowerCase();
    if (!groupedByNeighborhood.has(key)) {
      groupedByNeighborhood.set(key, { displayName, tables: [] });
    }
    groupedByNeighborhood.get(key)!.tables.push(table);
  });

  const neighborhoodGroups: NeighborhoodGroup[] = [];
  groupedByNeighborhood.forEach(({ displayName, tables }) => {
    // Sort by table id, not restaurant: the id never changes, so swapping a
    // table's restaurant doesn't make it jump to a different position.
    tables.sort((a, b) => a.id.localeCompare(b.id));
    const neighborhoodName = displayName;
    const totalCapacity = tables.reduce((sum, t) => sum + t.capacity, 0);
    const totalOccupied = tables.reduce((sum, t) => sum + t.users.length, 0);
    // Count assigned users in this neighborhood + unassigned users in this neighborhood
    const assignedBookings = tables.reduce((sum, t) => sum + t.users.length, 0);
    const unassignedBookings = unassignedUsers.filter(
      (user) => user.neighborhood?.toLowerCase() === neighborhoodName.toLowerCase()
    ).length;
    const totalBookings = assignedBookings + unassignedBookings;

    neighborhoodGroups.push({
      neighborhoodName,
      cityName: neighborhoodToCityMap.get(neighborhoodName.toLowerCase()) || "",
      tables,
      totalCapacity,
      totalOccupied,
      totalBookings,
    });
  });

  neighborhoodGroups.sort((a, b) => {
    const cityCompare = a.cityName.localeCompare(b.cityName);
    if (cityCompare !== 0) return cityCompare;
    return a.neighborhoodName.localeCompare(b.neighborhoodName);
  });

  const toggleNeighborhood = (neighborhoodName: string) => {
    setCollapsedNeighborhoods((prev) => {
      const next = new Set(prev);
      if (next.has(neighborhoodName)) {
        next.delete(neighborhoodName);
      } else {
        next.add(neighborhoodName);
      }
      return next;
    });
  };

  const getEditValues = (table: Table) => {
    return localEdits[table.id] ?? {
      restaurantId: table.restaurantId || "",
      reservationName: table.reservationName,
      capacity: table.capacity,
    };
  };

  const updateField = (table: Table, field: string, value: string | number) => {
    const current = getEditValues(table);
    setLocalEdits(prev => ({
      ...prev,
      [table.id]: { ...current, [field]: value },
    }));
  };

  const clearLocalEdit = (tableId: string) => {
    setLocalEdits((prev) => {
      const next = { ...prev };
      delete next[tableId];
      return next;
    });
  };

  const commitChanges = (table: Table, overrides?: Partial<{ restaurantId: string; reservationName: string; capacity: number }>) => {
    const current = getEditValues(table);
    const editData = { ...current, ...(overrides || {}) };

    const changed =
      editData.restaurantId !== (table.restaurantId || "") ||
      editData.reservationName !== table.reservationName ||
      editData.capacity !== table.capacity;

    if (!changed) {
      clearLocalEdit(table.id);
      return;
    }

    if (!editData.restaurantId || !editData.reservationName.trim() || editData.capacity < 1) {
      toast.error("Please fill in all fields with valid values");
      return;
    }

    updateTable.mutate(
      { tableId: table.id, data: editData },
      {
        onSuccess: () => {
          clearLocalEdit(table.id);
          onRefresh();
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to update table");
          clearLocalEdit(table.id);
        },
      }
    );
  };

  // Get restaurants for editing - filter by neighborhood and include current restaurant even if inactive
  const getRestaurantsForEdit = (neighborhoodName: string, currentRestaurantId?: string) => {
    const target = neighborhoodName.toLowerCase();
    // Include cafes and bars but always render them after restaurants (the
    // picker draws a divider before each group). Some neighborhoods (e.g.
    // Palm Springs) only have bar-typed venues, so excluding them entirely
    // would leave the admin no way to seat anyone there.
    const restaurantsInNeighborhood = allRestaurants.filter(
      (r) =>
        r.isActive !== false &&
        r.neighborhoodName?.toLowerCase() === target
    );
    const plainRestaurants = restaurantsInNeighborhood.filter((r) => venueGroup(r) === "restaurant");
    const cafes = restaurantsInNeighborhood.filter((r) => venueGroup(r) === "cafe");
    const bars = restaurantsInNeighborhood.filter((r) => venueGroup(r) === "bar");
    const sorted = [...plainRestaurants, ...cafes, ...bars];

    // If current restaurant exists and isn't in the list, add it on top.
    if (currentRestaurantId) {
      const currentRestaurant = allRestaurants.find((r) => r.id === currentRestaurantId);
      if (currentRestaurant && !sorted.find((r) => r.id === currentRestaurantId)) {
        return [currentRestaurant, ...sorted];
      }
    }

    return sorted;
  };

  const handleDelete = async (table: Table) => {
    if (table.users.length > 0) {
      toast.error("Cannot delete table with assigned users");
      return;
    }

    const confirmed = await confirm({
      title: "Delete Table",
      message: `Are you sure you want to delete "${table.reservationName}"?`,
      confirmText: "Delete",
      cancelText: "Cancel",
    });

    if (confirmed) {
      deleteTable.mutate(table.id, {
        onSuccess: () => {
          toast.success("Table deleted successfully");
          onRefresh();
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to delete table");
        },
      });
    }
  };

  const handleToggleReservationDone = async (table: Table) => {
    const newValue = table.reservationDone ? null : new Date().toISOString();
    
    updateTable.mutate(
      { 
        tableId: table.id, 
        data: { reservationDone: newValue } as any 
      },
      {
        onSuccess: () => {
          toast.success(newValue ? "Reservation marked as done" : "Reservation marked as pending");
          onRefresh();
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to update reservation status");
        },
      }
    );
  };

  const handleAddForNeighborhood = (neighborhoodName: string, capacity: number = 6) => {
    // Case-insensitive match — restaurant.neighborhoodName casing may differ
    // from the group's display name (which may be derived from user bookings).
    const target = neighborhoodName.toLowerCase();
    const activeInNeighborhood = allRestaurants.filter(
      (r) =>
        r.neighborhoodName?.toLowerCase() === target &&
        r.isActive !== false
    );
    // Prefer real restaurants; fall back to bars only if a neighborhood has
    // nothing else (e.g. Palm Springs).
    const nonBars = activeInNeighborhood.filter((r) => r.type !== "bar");
    const candidates = nonBars.length > 0 ? nonBars : activeInNeighborhood;

    const randomRestaurant = getRandomElement(candidates);
    if (!randomRestaurant) {
      toast.error(`No restaurants available in ${neighborhoodName}`);
      return;
    }

    // Get set of names already used by existing tables
    const usedNames = new Set(tables.map((t) => t.reservationName));

    // Create the table directly with specified capacity
    createTable.mutate(
      {
        eventTimeId: eventId,
        restaurantId: randomRestaurant.id,
        capacity,
        reservationName: getNextAvailableReservationName(usedNames),
      },
      {
        onSuccess: () => {
          toast.success("Table created successfully");
          onRefresh();
        },
        onError: (error: any) => {
          toast.error(error?.message || "Failed to create table");
        },
      }
    );
  };

  const handleBulkAddForNeighborhood = async (neighborhoodName: string, perTableCapacity: number) => {
    // Find the neighborhood group to get booking count and existing tables
    const group = neighborhoodGroups.find((g) => g.neighborhoodName === neighborhoodName);
    if (!group) {
      toast.error("Neighborhood not found");
      return;
    }
    
    const totalBookings = group.totalBookings;
    const existingTableCount = group.tables.length;
    
    // Calculate how many tables are needed to seat all bookings
    const tablesNeeded = Math.ceil(totalBookings / perTableCapacity);
    const tablesToCreate = tablesNeeded - existingTableCount;
    
    if (tablesToCreate <= 0) {
      toast.success(`Already have ${existingTableCount} table${existingTableCount !== 1 ? "s" : ""} for ${totalBookings} bookings (need ${tablesNeeded})`);
      return;
    }
    
    // Case-insensitive match — restaurant.neighborhoodName casing may differ
    // from the group's display name (which may be derived from user bookings).
    const target = neighborhoodName.toLowerCase();
    const activeInNeighborhood = allRestaurants.filter(
      (r) =>
        r.neighborhoodName?.toLowerCase() === target &&
        r.isActive !== false
    );
    // Prefer non-bars; fall back to bars when a neighborhood has nothing else.
    const nonBars = activeInNeighborhood.filter((r) => r.type !== "bar");
    const neighborhoodRestaurants = nonBars.length > 0 ? nonBars : activeInNeighborhood;

    if (neighborhoodRestaurants.length === 0) {
      toast.error(`No restaurants available in ${neighborhoodName}`);
      return;
    }

    setIsBulkCreating(true);
    
    // Get set of names already used by existing tables
    const usedNames = new Set(tables.map((t) => t.reservationName));
    
    let created = 0;
    let failed = 0;
    
    // Create tables sequentially to avoid race conditions with reservation names
    for (let i = 0; i < tablesToCreate; i++) {
      const randomRestaurant = getRandomElement(neighborhoodRestaurants);
      const reservationName = getNextAvailableReservationName(usedNames);
      usedNames.add(reservationName); // Mark as used for next iteration
      
      try {
        await new Promise<void>((resolve, reject) => {
          createTable.mutate(
            {
              eventTimeId: eventId,
              restaurantId: randomRestaurant.id,
              capacity: perTableCapacity,
              reservationName,
            },
            {
              onSuccess: () => {
                created++;
                resolve();
              },
              onError: (error: any) => {
                failed++;
                reject(error);
              },
            }
          );
        });
      } catch (error) {
        // Continue with remaining tables even if one fails
        console.error("Failed to create table:", error);
      }
    }
    
    setIsBulkCreating(false);
    
    if (created > 0) {
      toast.success(`Created ${created} table${created !== 1 ? "s" : ""} (${perTableCapacity}/table) for ${totalBookings} bookings`);
      onRefresh();
    }
    if (failed > 0) {
      toast.error(`Failed to create ${failed} table${failed !== 1 ? "s" : ""}`);
    }
    
    // Clear the bulk capacity input for this neighborhood
    setBulkCapacity((prev) => {
      const next = { ...prev };
      delete next[neighborhoodName];
      return next;
    });
  };

  if (neighborhoodGroups.length === 0) {
    return null;
  }

  return (
    <div className="card mb-8">
      <div
        className="flex items-center justify-between cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-base-content/60" />
          ) : (
            <ChevronRight className="w-5 h-5 text-base-content/60" />
          )}
          <h2 className="text-xl font-semibold">Table Management</h2>
          <div className="badge badge-neutral">
            {neighborhoodGroups.length} neighborhood{neighborhoodGroups.length !== 1 ? "s" : ""}
          </div>
        </div>
        {isExpanded && (() => {
          const allCollapsed = neighborhoodGroups.every((g) =>
            collapsedNeighborhoods.has(g.neighborhoodName)
          );
          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                if (allCollapsed) {
                  setCollapsedNeighborhoods(new Set());
                } else {
                  setCollapsedNeighborhoods(
                    new Set(neighborhoodGroups.map((g) => g.neighborhoodName))
                  );
                }
              }}
              className="btn btn-sm btn-ghost gap-1"
            >
              {allCollapsed ? (
                <>
                  <ChevronDown className="w-4 h-4" /> Expand all
                </>
              ) : (
                <>
                  <ChevronRight className="w-4 h-4" /> Collapse all
                </>
              )}
            </button>
          );
        })()}
      </div>

      {isExpanded && (
        <div className="mt-6 space-y-1">
          {neighborhoodGroups.map((group) => {
            const isCollapsed = collapsedNeighborhoods.has(group.neighborhoodName);
            return (
            <div key={group.neighborhoodName} className="border border-base-300 rounded-lg p-2 px-4">
              <div className="flex items-center justify-between">
                <div
                  className="flex items-center gap-2 cursor-pointer flex-1"
                  onClick={() => toggleNeighborhood(group.neighborhoodName)}
                >
                  {isCollapsed ? (
                    <ChevronRight className="w-4 h-4 text-base-content/60" />
                  ) : (
                    <ChevronDown className="w-4 h-4 text-base-content/60" />
                  )}
                  <div>
                    <h3 className="text-lg font-semibold capitalize">
                      {group.cityName && <span className="text-base-content/50">{group.cityName} — </span>}
                      {group.neighborhoodName}
                    </h3>
                    <div className="flex gap-4 text-sm text-base-content mt-1">
                      <span>{group.tables.length} tables</span>
                      <span>
                        {group.totalOccupied} / {group.totalCapacity} assigned
                      </span>
                      <span>{group.totalBookings} bookings</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* Bulk add based on bookings */}
                  <div className="flex items-center gap-1">
                    <input
                      type="number"
                      className="input input-sm input-bordered w-16 text-center"
                      min={1}
                      max={12}
                      value={bulkCapacity[group.neighborhoodName] ?? 6}
                      onChange={(e) => {
                        const value = parseInt(e.target.value) || 0;
                        setBulkCapacity((prev) => ({
                          ...prev,
                          [group.neighborhoodName]: value,
                        }));
                      }}
                      onClick={(e) => e.stopPropagation()}
                      title="Per-table capacity"
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const perTableCapacity = bulkCapacity[group.neighborhoodName] || 6;
                        handleBulkAddForNeighborhood(group.neighborhoodName, perTableCapacity);
                      }}
                      className="btn btn-sm btn-primary gap-1"
                      disabled={createTable.isPending || isBulkCreating}
                      title={(() => {
                        const cap = bulkCapacity[group.neighborhoodName] || 6;
                        const needed = Math.ceil(group.totalBookings / cap);
                        const toCreate = Math.max(0, needed - group.tables.length);
                        return `Create ${toCreate} table${toCreate !== 1 ? 's' : ''} (${cap}/table) for ${group.totalBookings} bookings`;
                      })()}
                    >
                      {isBulkCreating ? (
                        <span className="loading loading-spinner loading-xs"></span>
                      ) : (
                        <>
                          <Plus className="w-4 h-4" />
                          +{(() => {
                            const cap = bulkCapacity[group.neighborhoodName] || 6;
                            const needed = Math.ceil(group.totalBookings / cap);
                            return Math.max(0, needed - group.tables.length);
                          })()}
                        </>
                      )}
                    </button>
                  </div>
                  
                  <div className="divider divider-horizontal mx-0"></div>
                  
                  {/* Single table add */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleAddForNeighborhood(group.neighborhoodName, bulkCapacity[group.neighborhoodName] ?? 6);
                    }}
                    className="btn btn-sm btn-ghost gap-2"
                    disabled={createTable.isPending || isBulkCreating}
                  >
                    <Plus className="w-4 h-4" />
                    +1
                  </button>
                </div>
              </div>

              {!isCollapsed && <div className="overflow-x-auto mt-4">
                <table className="table table-sm w-full table-zebra">
                  <thead>
                    <tr className="border-b border-base-300">
                      <th>Restaurant Name</th>
                      <th>Reservation Name</th>
                      <th className="text-center">Usage</th>
                      <th className="text-center">Booking</th>
                      <th className="text-center">Reserved</th>
                      <th className="text-center">Lock</th>
                      <th className="text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {group.tables.map((table) => {
                      const values = getEditValues(table);
                      return (
                      <tr
                        key={table.id}
                        className={`
                          hover:bg-base-200/70 transition-colors h-12
                          ${table.isLocked ? 'opacity-60' : ''}
                        `}
                        style={{ minHeight: '48px' }}
                      >
                        <td className="font-medium max-w-md">
                          <div className="flex items-center gap-2 min-w-0">
                            <RestaurantSelectCell
                              currentRestaurantId={values.restaurantId}
                              currentRestaurantName={(() => {
                                const r = allRestaurants.find((r) => r.id === values.restaurantId);
                                return r ? r.name : table.restaurantName || "Unknown";
                              })()}
                              dollarTags={(() => {
                                const r = allRestaurants.find((r) => r.id === values.restaurantId);
                                if (r?.tags) return extractDollarTags(r.tags);
                                if (values.restaurantId === table.restaurantId)
                                  return extractDollarTags(table.restaurantTags);
                                return [];
                              })()}
                              availableRestaurants={getRestaurantsForEdit(group.neighborhoodName, table.restaurantId)}
                              isLocked={!!table.isLocked}
                              onSelect={(restaurantId) => {
                                updateField(table, "restaurantId", restaurantId);
                                commitChanges(table, { restaurantId });
                              }}
                              getPastVisits={getPastVisits}
                              tableUsers={table.users}
                            />
                            <UserPreferencesIndicator users={table.users} />
                          </div>
                        </td>
                        <td>
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              className="input input-sm input-ghost w-full hover:input-bordered focus:input-bordered"
                              value={values.reservationName}
                              onChange={(e) => updateField(table, "reservationName", e.target.value)}
                              onBlur={() => commitChanges(table)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                              }}
                              disabled={table.isLocked}
                            />
                            <button className="btn btn-xs btn-ghost" onClick={() => {
                              navigator.clipboard.writeText(values.reservationName);
                              toast.success("Copied to clipboard");
                            }}>
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                        <td className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            <span className={
                              table.users.length >= values.capacity
                                ? "text-success font-semibold"
                                : table.users.length > 0
                                ? "text-warning"
                                : "text-base-content/60"
                            }>
                              {table.users.length}
                            </span>
                            <span>/</span>
                            <input
                              type="number"
                              className="input input-sm input-ghost w-14 text-center hover:input-bordered focus:input-bordered"
                              value={values.capacity}
                              min="1"
                              onChange={(e) => updateField(table, "capacity", parseInt(e.target.value) || 1)}
                              onBlur={() => commitChanges(table)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                              }}
                              disabled={table.isLocked}
                            />
                          </div>
                        </td>
                        <td className="text-center">
                          <div className="py-1.5 flex gap-2 items-center justify-end">
                            {table.restaurantBookingLink ? (
                              <a
                                href={buildBookingLink(
                                  table.restaurantBookingLink,
                                  values.capacity,
                                  eventDate,
                                  eventTime,
                                  values.reservationName,
                                ) || table.restaurantBookingLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-xs btn-ghost gap-1 h-auto min-h-0 py-1"
                                title="Open booking link"
                              > Book
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            ) : (
                              <span className="text-base-content text-xs">-</span>
                            )}
                          </div>
                        </td>
                        <td className="text-center">
                          <div className="py-1.5 flex items-center justify-center gap-1">
                            <button
                              onClick={() => handleToggleReservationDone(table)}
                              className={`btn btn-xs gap-1 h-auto min-h-0 py-1 ${
                                table.reservationDone
                                  ? 'btn-success'
                                  : 'btn-ghost'
                              }`}
                              disabled={updateTable.isPending}
                              title={
                                table.reservationDone
                                  ? `Reserved on ${new Date(table.reservationDone).toLocaleDateString()}`
                                  : "Mark as reserved"
                              }
                            >
                              {table.reservationDone ? (
                                <SquareCheckBig className="w-4 h-4" />
                              ) : (
                                <Square className="w-4 h-4" />
                              )}
                            </button>
                            {table.reservationDone && (
                              <a
                                href={`https://www.opentable.com/user/dining-dashboard?searchReservation=${encodeURIComponent(values.reservationName)}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-xs btn-ghost gap-1 h-auto min-h-0 py-1"
                                title="Open reservation on OpenTable dining dashboard"
                              >
                                <ExternalLink className="w-3 h-3" />
                              </a>
                            )}
                          </div>
                        </td>
                        <td className="text-center">
                          <label
                            className="flex items-center justify-center cursor-pointer"
                            title={table.isLocked ? "Unlock table" : "Lock table"}
                          >
                            <input
                              type="checkbox"
                              className="checkbox checkbox-sm"
                              checked={table.isLocked}
                              onChange={() => onToggleLock?.(table.id)}
                              disabled={!onToggleLock}
                            />
                            <Lock
                              className={`w-3 h-3 ml-1 ${
                                table.isLocked ? "text-warning" : "text-base-content/30"
                              }`}
                            />
                          </label>
                        </td>
                        <td className="text-right">
                          <div className="flex gap-1 justify-end">
                            <button
                              onClick={() => handleDelete(table)}
                              className="btn btn-xs btn-ghost text-error"
                              disabled={
                                table.users.length > 0 ||
                                deleteTable.isPending
                              }
                              title={
                                table.users.length > 0
                                  ? "Cannot delete table with users"
                                  : "Delete table"
                              }
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>}
            </div>
          );
          })}
        </div>
      )}
    </div>
  );
}

